import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { LogoMark } from "@/components/Logo";
import IntroVideo, { shouldPlayIntro } from "@/components/IntroVideo";
import { isNativeApp } from "@/lib/platform";
import { PremiumProvider } from "@/components/Paywall";

import Login from "@/pages/Login";
import PatientHome from "@/pages/patient/PatientHome";
import PatientActivities from "@/pages/patient/PatientActivities";
import PatientRecords from "@/pages/patient/PatientRecords";
import PatientMessages from "@/pages/patient/PatientMessages";
import PatientMaterials from "@/pages/patient/PatientMaterials";
import PatientAgenda from "@/pages/patient/PatientAgenda";
import SupportNetwork from "@/pages/patient/SupportNetwork";
import NeedSupport from "@/pages/patient/NeedSupport";
import ProDashboard from "@/pages/pro/ProDashboard";
import ProPatients from "@/pages/pro/ProPatients";
import ProPatientDetail from "@/pages/pro/ProPatientDetail";
import ProAgenda from "@/pages/pro/ProAgenda";
import AdminHome from "@/pages/admin/AdminHome";
import MinhaJornada from "@/pages/patient/MinhaJornada";
import Perfil from "@/pages/Perfil";

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC]">
      <div className="animate-soft-pulse"><LogoMark size={56} /></div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function RoleRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  const role = user?.role;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />

      {role === "patient" && (
        <>
          <Route path="/" element={<Protected><PatientHome /></Protected>} />
          <Route path="/atividades" element={<Protected><PatientActivities /></Protected>} />
          <Route path="/registro" element={<Protected><PatientRecords /></Protected>} />
          <Route path="/mensagens" element={<Protected><PatientMessages /></Protected>} />
          <Route path="/conteudos" element={<Protected><PatientMaterials /></Protected>} />
          <Route path="/agenda" element={<Protected><PatientAgenda /></Protected>} />
          <Route path="/rede-apoio" element={<Protected><SupportNetwork /></Protected>} />
          <Route path="/apoio" element={<Protected><NeedSupport /></Protected>} />
          <Route path="/jornada" element={<Protected><MinhaJornada /></Protected>} />
          <Route path="/perfil" element={<Protected><Perfil /></Protected>} />
        </>
      )}

      {(role === "professional" || role === "org_admin") && (
        <>
          <Route path="/" element={<Protected><ProDashboard /></Protected>} />
          <Route path="/pacientes" element={<Protected><ProPatients /></Protected>} />
          <Route path="/pacientes/:id" element={<Protected><ProPatientDetail /></Protected>} />
          <Route path="/agenda" element={<Protected><ProAgenda /></Protected>} />
          <Route path="/conteudos" element={<Protected><PatientMaterials /></Protected>} />
          <Route path="/perfil" element={<Protected><Perfil /></Protected>} />
          {role === "org_admin" && <Route path="/admin" element={<Protected><AdminHome /></Protected>} />}
        </>
      )}

      <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
    </Routes>
  );
}

export default function App() {
  // Opening video plays only on native/installed mobile apps, right after the
  // OS splash screen — never on the web. Falls through to the app on any error.
  const [showIntro, setShowIntro] = useState(() => isNativeApp() && shouldPlayIntro());
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          {showIntro && <IntroVideo onFinish={() => setShowIntro(false)} />}
          <PremiumProvider>
            <RoleRoutes />
          </PremiumProvider>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
